/**
 * Preview Renderer
 * 
 * Component for rendering the page in preview mode without editing controls.
 * Shows how the page will look when published.
 */

import React from 'react';
import { Box, Paper, Typography, Container } from '@mui/material';
import ComponentRenderer from './renderers/ComponentRenderer';
import { PageDoc, Section, Row, Column } from '../../types/page-builder.types';
import { convertStyleToSx, mergeSx } from '../../utils/style-converter';

interface PreviewRendererProps {
  pageDoc: PageDoc;
  showPageFrame?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
}

export const PreviewRenderer: React.FC<PreviewRendererProps> = ({
  pageDoc,
  showPageFrame = true,
  maxWidth = 'lg',
}) => {
  const ContentWrapper: React.FC<any> = maxWidth ? Container : Box;
  const wrapperProps = maxWidth ? { maxWidth } : {};

  const renderContent = () => (
    <Box sx={{ py: showPageFrame ? 3 : 0 }}>
      {/* Page Header - Only show if there's actual content; don't use view metadata as page title */}

      {/* Page Content */}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {pageDoc.layout.map((section) => (
          <PreviewSection key={section.id} section={section} />
        ))}
      </Box>

      {/* Empty State */}
      {pageDoc.layout.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
            color: 'text.secondary',
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" gutterBottom>
            Empty Page
          </Typography>
          <Typography variant="body2">
            This page doesn't have any content yet.
          </Typography>
        </Box>
      )}
    </Box>
  );

  if (showPageFrame) {
    return (
      <Paper 
        sx={{ 
          minHeight: '100vh',
          borderRadius: 0,
          bgcolor: 'background.default',
          ...convertStyleToSx(pageDoc.style),
        }}
      >
        <ContentWrapper {...wrapperProps}>
          {renderContent()}
        </ContentWrapper>
      </Paper>
    );
  }

  return (
    <Box sx={{ ...convertStyleToSx(pageDoc.style) }}>
      <ContentWrapper {...wrapperProps}>
        {renderContent()}
      </ContentWrapper>
    </Box>
  );
};

// Preview Section Component
interface PreviewSectionProps {
  section: Section;
}

const PreviewSection: React.FC<PreviewSectionProps> = ({ section }) => {
  // Skip rendering if section is hidden
  if (section.visible === false) return null;

  // Default styles based on section kind
  const defaultStyles = {
    width: '100%',
    ...(section.kind === 'hero' && {
      bgcolor: 'primary.main',
      color: 'primary.contrastText',
      py: 6,
      px: 3,
      borderRadius: 2,
    }),
    ...((section.kind as any) === 'feature' && {
      bgcolor: 'grey.50',
      py: 4,
      px: 3,
      borderRadius: 2,
    }),
  };

  // Convert styles and remove only default editor scaffolding
  const sectionStyles = convertStyleToSx(section.style);
  const cleanedSectionStyles: Record<string, any> = {
    ...sectionStyles,
  };

  // Only remove default scaffolding if it matches the exact default values
  if (section.kind === 'container' && section.style?.border) {
    const border = section.style.border;
    // Remove if it's the default editor scaffolding (1px solid divider with 16px padding and 2px borderRadius)
    if (border.width === 1 && border.style === 'solid' && border.color === 'divider' &&
        section.style.padding === 16 && section.style.borderRadius === 2) {
      cleanedSectionStyles.border = undefined;
      cleanedSectionStyles.borderWidth = undefined;
      cleanedSectionStyles.borderStyle = undefined;
      cleanedSectionStyles.borderColor = undefined;
      cleanedSectionStyles.borderRadius = undefined;
      cleanedSectionStyles.p = undefined;
    }
  }

  // Merge default styles with cleaned custom styles (custom takes precedence)
  const sectionSx = mergeSx(defaultStyles, cleanedSectionStyles);

  return (
    <Box sx={sectionSx}>
      {section.rows.map((row) => (
        <PreviewRow key={row.id} row={row} />
      ))}
    </Box>
  );
};

// Preview Row Component
interface PreviewRowProps {
  row: Row;
}

const PreviewRow: React.FC<PreviewRowProps> = ({ row }) => {
  // Skip rendering if row is hidden
  if (row.visible === false) return null;

  // Calculate responsive grid layout
  const totalWidth = row.columns.reduce((sum, col) => sum + (col.width || 1), 0);
  
  // Default row styles
  const defaultStyles = {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
  };

  // Convert styles - rows don't have default scaffolding borders, so keep all styles
  const rowStyles = convertStyleToSx(row.style);

  // Merge default styles with custom styles
  const rowSx = mergeSx(defaultStyles, rowStyles);

  return (
    <Box sx={rowSx}>
      {row.columns.map((column) => (
        <PreviewColumn 
          key={column.id} 
          column={column} 
          widthRatio={(column.width || 1) / totalWidth}
        />
      ))}
    </Box>
  );
};

// Preview Column Component
interface PreviewColumnProps {
  column: Column;
  widthRatio: number;
}

const PreviewColumn: React.FC<PreviewColumnProps> = ({ column, widthRatio }) => {
  // Skip rendering if column is hidden
  if (column.visible === false) return null;

  // Default column styles
  const defaultStyles = {
    flex: { xs: '1 1 100%', md: `${widthRatio} 1 0%` },
    display: 'flex',
    flexDirection: 'column',
  };

  // Convert styles and remove only default editor scaffolding
  const columnStyles = convertStyleToSx(column.style);
  const cleanedColumnStyles: Record<string, any> = {
    ...columnStyles,
  };

  // Only remove default scaffolding if it matches the exact default values
  if (column.style?.border) {
    const border = column.style.border;
    // Remove if it's the default editor scaffolding (1px dashed divider with 16px padding, 1px borderRadius, 100px minHeight)
    if (border.width === 1 && border.style === 'dashed' && border.color === 'divider' &&
        column.style.padding === 16 && column.style.borderRadius === 1 && column.style.minHeight === 100) {
      cleanedColumnStyles.border = undefined;
      cleanedColumnStyles.borderWidth = undefined;
      cleanedColumnStyles.borderStyle = undefined;
      cleanedColumnStyles.borderColor = undefined;
      cleanedColumnStyles.borderRadius = undefined;
      cleanedColumnStyles.p = undefined;
      cleanedColumnStyles.minHeight = undefined;
    }
  }

  // Merge default styles with cleaned custom styles
  const columnSx = mergeSx(defaultStyles, cleanedColumnStyles);

  return (
    <Box sx={columnSx}>
      {column.blocks.map((block) => (
        <Box key={block.id} sx={{ width: '100%' }}>
          <ComponentRenderer
            block={block}
            isSelected={false}
            readonly={true}
          />
        </Box>
      ))}

      {/* Don't show empty column indicators in live view */}
    </Box>
  );
};

export default PreviewRenderer;